// =============================================================================
// StatementDetailPage — Slice 7d.7 Piece 3 frontend
// =============================================================================
// One statement, with everything you need to act on it:
//
//   - Summary: number, period, status pill, frozen totals
//   - Chauffeur block: name, email, VAT (if registered)
//   - Lines: bookings (or shifts) + expenses, mirrors the PDF layout but
//            simpler — no per-line edit on issued statements (lines are
//            frozen by design; void+regen if you need changes)
//   - Action buttons (owner only):
//       · Download PDF (binary stream from /statements/:id/pdf?download=1)
//       · Email statement (modal — recipients picker + send)
//       · Mark paid (modal — optional note)
//       · Void (modal — required reason, lines released back to pool)
//
// Status transitions (status-aware action availability):
//   sent  → email, mark paid, void
//   paid  → no actions (terminal — financial integrity)
//   voided → no actions (terminal — generate a new statement instead)
//
// Owner-only by route guard. Non-owners shouldn't reach this page.
// =============================================================================

import EmailPreviewModal from '../../components/email/EmailPreviewModal.jsx';
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Download,
    Mail,
    CheckCircle2,
    Ban,
    FileText,
    AlertTriangle,
    Trash2,
    Plus,
    Send,
    Clock3,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import {
    PageHeader,
    LoadingPage,
    ErrorPage,
    StatusPill,
} from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Textarea } from '../../components/ui/Form.jsx';

export function StatementDetailPage() {
    const { id } = useParams();
    const api = useApi();
    const { isOwner, isLoading: userLoading } = useCurrentUser();
    
    const [emailing, setEmailing] = useState(false);
    const [markingSent, setMarkingSent] = useState(false);
    const [markingPaid, setMarkingPaid] = useState(false);
    const [voiding, setVoiding] = useState(false);

    const stmtQuery = useQuery({
        queryKey: ['statement', id],
        queryFn: () => api.get(`/statements/${id}`),
        enabled: isOwner && Boolean(id),
    });

    if (userLoading) return <LoadingPage />;
    if (!isOwner) {
        return (
            <div className="p-6">
                <p className="text-sm text-ink-subtle">Owner access required.</p>
            </div>
        );
    }
    if (stmtQuery.isLoading) return <LoadingPage />;
    if (stmtQuery.isError) {
        return <ErrorPage message={stmtQuery.error?.message} onRetry={stmtQuery.refetch} />;
    }

const { statement, lines } = stmtQuery.data;
    // Three-way split mirrors the pool view and PDF: fares (positive),
    // expenses (positive, gross), deductions (negative).
    const fareLines = lines.filter(l => l.line_type === 'fare' || l.line_type == null);
    const expenseLines = lines.filter(l => l.line_type === 'expense');
    const deductionLines = lines.filter(l => l.line_type === 'deduction');

 const isPerHour = statement.chauffeur_pay_type === 'per_hour';
    const canAct = statement.status === 'sent' || statement.status === 'pending';

    const backHref = `/chauffeurs/${statement.chauffeur_id}/statements`;

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3 flex-wrap">
                        <span>Statement #{statement.statement_number}</span>
                        <StatusBadge status={statement.status} />
                    </span>
                }
                subtitle={
                    <span>
                        {statement.chauffeur_first_name} {statement.chauffeur_last_name}
                        {' · '}
                        {formatDate(statement.period_start)} – {formatDate(statement.period_end)}
                    </span>
                }
                backTo={backHref}
                backLabel="Statements"
               actions={
                    <ActionButtons
                        statement={statement}
                        canAct={canAct}
                        onEmail={() => setEmailing(true)}
                        onMarkSent={() => setMarkingSent(true)}
                        onMarkPaid={() => setMarkingPaid(true)}
                        onVoid={() => setVoiding(true)}
                    />
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left column — chauffeur + summary */}
                <div className="lg:col-span-1 space-y-5">
                    <ChauffeurBlock statement={statement} />
                    <SummaryCard statement={statement} />
                    {statement.status === 'paid' && <PaidBlock statement={statement} />}
                    {statement.status === 'voided' && <VoidedBlock statement={statement} />}
                </div>
{/* Right column — lines + expenses + deductions */}
                <div className="lg:col-span-2 space-y-5">
                    <LinesCard
                        title={isPerHour ? 'Shifts' : 'Bookings'}
                        lines={fareLines}
                        isPerHour={isPerHour}
                        emptyText="No fare lines on this statement."
                    />
                    {expenseLines.length > 0 && (
                        <ExpensesCard lines={expenseLines} />
                    )}
                    {deductionLines.length > 0 && (
                        <DeductionsCard lines={deductionLines} />
                    )}
                </div>
            </div>

{emailing && (
                <EmailPreviewModal
                    emails={[{
                        id: 'statement',
                        label: `Statement #${statement.statement_number}`,
                        previewParams: { kind: 'statement', statement_id: statement.id },
                        sendEndpoint: `/statements/${statement.id}/email`,
                        sendBody: (recipients) => ({ recipients }),
                    }]}
                    title="Email statement"
                    description={`Send statement #${statement.statement_number} as a PDF attachment.`}
                    onClose={() => setEmailing(false)}
                    onSent={() => {
                        setEmailing(false);
                        stmtQuery.refetch();
                    }}
                />
            )}
{markingSent && (
                <MarkSentModal
                    statement={statement}
                    open
                    onClose={() => setMarkingSent(false)}
                    onMarked={() => {
                        setMarkingSent(false);
                        stmtQuery.refetch();
                    }}
                />
            )}

            {markingPaid && (
                <MarkPaidModal
                    statement={statement}
                    open
                    onClose={() => setMarkingPaid(false)}
                    onMarked={() => {
                        setMarkingPaid(false);
                        stmtQuery.refetch();
                    }}
                />
            )}

            {voiding && (
                <VoidModal
                    statement={statement}
                    open
                    onClose={() => setVoiding(false)}
                    onVoided={() => {
                        setVoiding(false);
                        stmtQuery.refetch();
                    }}
                />
            )}
        </div>
    );
}

// =============================================================================
// Top-right action buttons in PageHeader
// =============================================================================
function ActionButtons({ statement, canAct, onEmail, onMarkSent, onMarkPaid, onVoid }) {
    const { getToken } = useAuth();
    const isPending = statement.status === 'pending';
    const isSent = statement.status === 'sent';

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <Button
                variant="secondary"
                onClick={() => downloadPdf(getToken, statement)}
            >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Download PDF</span>
            </Button>
            {canAct && (
                <>
                    <Button variant="secondary" onClick={onEmail}>
                        <Mail className="h-4 w-4" />
                        <span className="hidden sm:inline">Email</span>
                    </Button>
                    {isPending && (
                        <Button variant="secondary" onClick={onMarkSent}>
                            <Send className="h-4 w-4" />
                            <span className="hidden sm:inline">Mark as sent</span>
                        </Button>
                    )}
                    {isSent && (
                        <Button variant="primary" onClick={onMarkPaid}>
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="hidden sm:inline">Mark paid</span>
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        className="text-status-danger hover:text-status-danger"
                        onClick={onVoid}
                    >
                        <Ban className="h-4 w-4" />
                        <span className="hidden sm:inline">Void</span>
                    </Button>
                </>
            )}
        </div>
    );
}

// =============================================================================
// Chauffeur block — name, address, VAT info if applicable
// =============================================================================
function ChauffeurBlock({ statement }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Chauffeur</h2>
            </div>
            <div className="card-body">
                <Link
                    to={`/chauffeurs/${statement.chauffeur_id}`}
                    className="text-sm font-medium text-ink hover:underline"
                >
                    {statement.chauffeur_first_name} {statement.chauffeur_last_name}
                </Link>
                {statement.chauffeur_email && (
                    <div className="text-xs text-ink-subtle mt-1 break-all">
                        {statement.chauffeur_email}
                    </div>
                )}
                {statement.chauffeur_home_address && (
                    <div className="text-xs text-ink-subtle mt-1 break-words">
                        {statement.chauffeur_home_address}
                    </div>
                )}
                <div className="mt-3 pt-3 border-t border-paper-border-soft text-xs text-ink-subtle space-y-1">
                    <div>
                        Pay type: <span className="text-ink">
                            {statement.chauffeur_pay_type === 'per_hour' ? 'Per hour' : 'Per job'}
                        </span>
                    </div>
                    <div>
                        VAT: <span className="text-ink">
                            {statement.chauffeur_vat_registered
                                ? `Registered${statement.chauffeur_vat_number ? ` · ${statement.chauffeur_vat_number}` : ''}`
                                : 'Not registered'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// Summary card — frozen totals
// =============================================================================
function SummaryCard({ statement }) {
    const sub = (statement.gross_total_pence || 0) - (statement.vat_total_pence || 0);

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Summary</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Frozen at issue. To change, void and regenerate.
                </p>
            </div>
            <div className="card-body">
                <dl className="space-y-2">
                    <Row label="Issued" value={formatDateTime(statement.issued_at)} />
                    <Row label="Lines" value={statement.line_count} />
                    {statement.emailed_at && (
                        <Row
                            label="Last emailed"
                            value={formatDateTime(statement.emailed_at)}
                        />
                    )}
                </dl>

                <div className="mt-4 pt-4 border-t border-paper-border-soft space-y-2 text-sm">
                    <div className="flex justify-between text-ink-subtle">
                        <span>Sub-total</span>
                        <span className="text-ink">{formatPence(sub)}</span>
                    </div>
                    {(statement.vat_total_pence || 0) > 0 && (
                        <div className="flex justify-between text-ink-subtle">
                            <span>VAT</span>
                            <span className="text-ink">{formatPence(statement.vat_total_pence)}</span>
                        </div>
                    )}
             {(statement.expenses_total_pence || 0) > 0 && (
                        <div className="flex justify-between text-ink-subtle">
                            <span>Expenses</span>
                            <span className="text-ink">{formatPence(statement.expenses_total_pence)}</span>
                        </div>
                    )}
                    {(statement.deductions_total_pence || 0) > 0 && (
                        <div className="flex justify-between text-red-700">
                            <span>Deductions</span>
                            <span>−{formatPence(statement.deductions_total_pence)}</span>
                        </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-paper-border-soft font-semibold">
                        <span className="text-ink">Grand total</span>
                        <span className="text-ink">{formatPence(statement.grand_total_pence)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div>
            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">{label}</dt>
            <dd className="text-sm text-ink mt-0.5">{value || '—'}</dd>
        </div>
    );
}

function PaidBlock({ statement }) {
    return (
        <div className="card border-emerald-200 bg-emerald-50/40">
            <div className="card-body">
                <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                        <div className="font-medium text-emerald-900">Paid</div>
                        <div className="text-emerald-800/80 mt-0.5">
                            {formatDateTime(statement.paid_at)}
                        </div>
                        {statement.paid_at_note && (
                            <div className="text-emerald-800/70 mt-1.5 italic">
                                {statement.paid_at_note}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function VoidedBlock({ statement }) {
    return (
        <div className="card border-amber-200 bg-amber-50/40">
            <div className="card-body">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-status-warning mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                        <div className="font-medium text-status-warning">Voided</div>
                        <div className="text-amber-800/80 mt-0.5">
                            {formatDateTime(statement.voided_at)}
                        </div>
                        {statement.voided_reason && (
                            <div className="text-amber-800/80 mt-1.5 italic break-words">
                                {statement.voided_reason}
                            </div>
                        )}
                        <div className="text-amber-800/70 mt-2">
                            Lines have been released back to the pool.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// Lines card — fares table (per_job vs per_hour shape)
// =============================================================================
function LinesCard({ title, lines, isPerHour, emptyText }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">{title}</h2>
            </div>
            {lines.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle">{emptyText}</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-paper-border-soft text-xs text-ink-subtle uppercase tracking-wider">
                                {isPerHour ? (
                                    <>
                                        <th className="text-left px-6 py-2 font-medium">Date</th>
                                        <th className="text-left px-6 py-2 font-medium">Hours</th>
                                        <th className="text-left px-6 py-2 font-medium">Description</th>
                                        <th className="text-right px-6 py-2 font-medium">Amount</th>
                                    </>
                                ) : (
                                    <>
                                        <th className="text-left px-6 py-2 font-medium">Ref</th>
                                        <th className="text-left px-6 py-2 font-medium">Date</th>
                                        <th className="text-left px-6 py-2 font-medium">Passenger</th>
                                        <th className="text-left px-6 py-2 font-medium">Itinerary</th>
                                        <th className="text-right px-6 py-2 font-medium">Amount</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((line) => (
                                <tr
                                    key={line.id}
                                    className="border-b border-paper-border-soft last:border-b-0"
                                >
                                    {isPerHour ? (
                                        <PerHourCells line={line} />
                                    ) : (
                                        <PerJobCells line={line} />
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function PerHourCells({ line }) {
    const hrs = line.hours != null ? Number(line.hours) : null;
    return (
        <>
            <td className="px-6 py-3 align-top">{formatDate(line.effective_date)}</td>
            <td className="px-6 py-3 align-top">{hrs != null ? `${hrs}` : '—'}</td>
            <td className="px-6 py-3 align-top text-ink-subtle">
                {isCustomLabel(line.label) ? line.label : ''}
            </td>
            <td className="px-6 py-3 align-top text-right font-medium">
                {formatGBP(line.amount_ex_vat)}
            </td>
        </>
    );
}

function PerJobCells({ line }) {
    return (
        <>
            <td className="px-6 py-3 align-top text-xs font-mono text-ink-subtle">
                {line.ride_ref || '—'}
            </td>
            <td className="px-6 py-3 align-top">{formatDate(line.effective_date)}</td>
            <td className="px-6 py-3 align-top">{line.pax_name || '—'}</td>
            <td className="px-6 py-3 align-top text-ink-subtle">
                {buildItinerary(line)}
            </td>
            <td className="px-6 py-3 align-top text-right font-medium">
                {formatGBP(line.amount_ex_vat)}
            </td>
        </>
    );
}

// =============================================================================
// Expenses card — Date / Description / Amount
// =============================================================================
function ExpensesCard({ lines }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Driver expenses</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Reimbursements (gross — VAT included).
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-paper-border-soft text-xs text-ink-subtle uppercase tracking-wider">
                            <th className="text-left px-6 py-2 font-medium">Date</th>
                            <th className="text-left px-6 py-2 font-medium">Description</th>
                            <th className="text-right px-6 py-2 font-medium">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line) => (
                            <tr
                                key={line.id}
                                className="border-b border-paper-border-soft last:border-b-0"
                            >
                                <td className="px-6 py-3 align-top">
                                    {formatDate(line.effective_date || line.created_at)}
                                </td>
                                <td className="px-6 py-3 align-top">{line.label || '—'}</td>
                                <td className="px-6 py-3 align-top text-right font-medium">
                                    {formatGBP(line.amount_ex_vat)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// Deductions card — Date / Description / Amount (subtracted from total)
// =============================================================================
function DeductionsCard({ lines }) {
    return (
        <div className="card border-red-200/60">
            <div className="card-header bg-red-50/40">
                <h2 className="text-sm font-semibold text-red-900">Deductions</h2>
                <p className="text-xs text-red-700/80 mt-0.5">
                    Subtracted from chauffeur pay (gross — incl. VAT where applicable).
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-paper-border-soft text-xs text-ink-subtle uppercase tracking-wider">
                            <th className="text-left px-6 py-2 font-medium">Date</th>
                            <th className="text-left px-6 py-2 font-medium">Description</th>
                            <th className="text-right px-6 py-2 font-medium">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line) => {
                            const ex = Number(line.amount_ex_vat) || 0;
                            const rate = Number(line.vat_rate) || 0;
                            const gross = ex * (1 + rate);
                            return (
                                <tr
                                    key={line.id}
                                    className="border-b border-paper-border-soft last:border-b-0"
                                >
                                    <td className="px-6 py-3 align-top">
                                        {formatDate(line.effective_date || line.created_at)}
                                    </td>
                                    <td className="px-6 py-3 align-top">{line.label || '—'}</td>
                                    <td className="px-6 py-3 align-top text-right font-medium text-red-700">
                                        −{formatGBP(gross)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// Email modal — recipients picker (default = chauffeur email) + send
// =============================================================================
function EmailModal({ statement, open, onClose, onSent }) {
    const api = useApi();
    const queryClient = useQueryClient();

    // Initial recipient list = chauffeur email if we have it, else empty
    const initialRecipients = statement.chauffeur_email ? [statement.chauffeur_email] : [];
    const [recipients, setRecipients] = useState(initialRecipients);
    const [newRecipient, setNewRecipient] = useState('');

    const sendMutation = useMutation({
        mutationFn: () =>
            api.post(`/statements/${statement.id}/email`, { recipients }),
        onSuccess: () => {
            toast.success(
                `Statement emailed to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`
            );
            queryClient.invalidateQueries({ queryKey: ['statement', statement.id] });
            onSent();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Send failed';
            toast.error(msg);
        },
    });

    function addRecipient() {
        const trimmed = newRecipient.trim();
        if (!trimmed) return;
        // Light validation — backend does the real check
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            toast.error('Please enter a valid email address');
            return;
        }
        if (recipients.includes(trimmed)) {
            toast('That address is already in the list');
            return;
        }
        setRecipients([...recipients, trimmed]);
        setNewRecipient('');
    }

    function removeRecipient(email) {
        setRecipients(recipients.filter(r => r !== email));
    }

    const canSend = recipients.length > 0 && !sendMutation.isPending;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Email statement"
            description={`Send statement #${statement.statement_number} as a PDF attachment.`}
            size="md"
        >
            <div className="space-y-3">
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Recipients
                    </label>
                    {recipients.length === 0 ? (
                        <p className="text-sm text-ink-subtle italic">No recipients added yet.</p>
                    ) : (
                        <ul className="space-y-1.5">
                            {recipients.map((email) => (
                                <li
                                    key={email}
                                    className="flex items-center justify-between gap-2 px-3 py-2 bg-paper-hover/50 border border-paper-border-soft rounded-md"
                                >
                                    <span className="text-sm text-ink break-all">{email}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeRecipient(email)}
                                        className="text-ink-muted hover:text-status-danger"
                                        aria-label={`Remove ${email}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Add recipient
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="email"
                            value={newRecipient}
                            onChange={(e) => setNewRecipient(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addRecipient();
                                }
                            }}
                            placeholder="email@example.com"
                            className="flex-1 px-3 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            disabled={sendMutation.isPending}
                        />
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={addRecipient}
                            disabled={!newRecipient.trim() || sendMutation.isPending}
                        >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                        </Button>
                    </div>
                    <p className="text-xs text-ink-subtle mt-1">
                        Press Enter to add. Each recipient receives the same email with the PDF attached.
                    </p>
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => sendMutation.mutate()}
                    disabled={!canSend}
                    loading={sendMutation.isPending}
                >
                    <Mail className="h-4 w-4" />
                    Send
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Mark sent modal — for statements delivered outside the email flow
// (printed + handed over, posted, WhatsApp, etc). Transitions pending → sent
// without firing email.
// =============================================================================
function MarkSentModal({ statement, open, onClose, onMarked }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: () => api.post(`/statements/${statement.id}/mark-sent`, {}),
        onSuccess: () => {
            toast.success('Statement marked as sent');
            queryClient.invalidateQueries({ queryKey: ['statement', statement.id] });
            queryClient.invalidateQueries({ queryKey: ['statements', statement.chauffeur_id] });
            onMarked();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not mark as sent';
            toast.error(msg);
        },
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Mark statement as sent?"
            description={`For statements delivered outside email — printed + handed over, posted, WhatsApp, etc. Use the Email button instead if you want to send via email.`}
            size="md"
        >
            <div className="text-sm text-ink-subtle">
                Statement #{statement.statement_number} will move to <strong className="text-ink">Sent</strong> status. After that, the next step is Mark paid once you've paid the chauffeur.
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <Send className="h-4 w-4" />
                    Mark as sent
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Mark paid modal
// =============================================================================
function MarkPaidModal({ statement, open, onClose, onMarked }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [note, setNote] = useState('');

    const mutation = useMutation({
        mutationFn: () =>
            api.post(`/statements/${statement.id}/mark-paid`, {
                paid_at_note: note.trim() || null,
            }),
        onSuccess: () => {
            toast.success('Statement marked as paid');
            queryClient.invalidateQueries({ queryKey: ['statement', statement.id] });
            queryClient.invalidateQueries({ queryKey: ['statements', statement.chauffeur_id] });
            onMarked();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not mark paid';
            toast.error(msg);
        },
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Mark statement as paid?"
            description={`Statement #${statement.statement_number} will be locked. This cannot be undone — paid statements cannot be voided.`}
            size="md"
        >
            <Textarea
                label="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. BACS ref 123456, paid 12 May 2026"
                rows={3}
                hint="Optional context for the audit log."
                disabled={mutation.isPending}
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <CheckCircle2 className="h-4 w-4" />
                    Mark paid
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Void modal
// =============================================================================
function VoidModal({ statement, open, onClose, onVoided }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [reason, setReason] = useState('');

    const mutation = useMutation({
        mutationFn: () =>
            api.post(`/statements/${statement.id}/void`, { reason: reason.trim() }),
        onSuccess: () => {
            toast.success('Statement voided. Lines released back to the pool.');
            queryClient.invalidateQueries({ queryKey: ['statement', statement.id] });
            queryClient.invalidateQueries({ queryKey: ['statements', statement.chauffeur_id] });
            queryClient.invalidateQueries({ queryKey: ['statement-pool', statement.chauffeur_id] });
            onVoided();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not void';
            toast.error(msg);
        },
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Void statement?"
            description={`Statement #${statement.statement_number} will be marked voided. The ${statement.line_count} lines on it will be released back into the pool, ready to be regenerated. The voided statement stays on file as an audit record.`}
            size="md"
        >
            <Textarea
                label="Reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Wrong period, duplicate generation, requested by chauffeur"
                rows={3}
                disabled={mutation.isPending}
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                    disabled={!reason.trim()}
                >
                    <Ban className="h-4 w-4" />
                    Void
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// PDF download — fetches with auth, opens as blob in a new tab
// =============================================================================
async function downloadPdf(getToken, statement) {
    try {
        const token = await getToken();
        const res = await fetch(`/api/statements/${statement.id}/pdf?download=1`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || `Failed (${res.status})`);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeName = `${statement.chauffeur_first_name}-${statement.chauffeur_last_name}`
            .replace(/[^a-zA-Z0-9-]/g, '_');
        a.download = `Lanz-CTS-Statement-${statement.statement_number}-${safeName}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Statement downloaded');
    } catch (err) {
        toast.error(err.message || 'Could not download PDF');
    }
}

// =============================================================================
// Status badge — colour-coded pill matching dispatch UI conventions
// =============================================================================
function StatusBadge({ status }) {
    const config = {
        pending: { tone: 'warning', label: 'Pending' },
        sent:    { tone: 'neutral', label: 'Sent' },
        paid:    { tone: 'success', label: 'Paid' },
        voided:  { tone: 'warning', label: 'Voided' },
    }[status] || { tone: 'neutral', label: status };

    return <StatusPill status={config.tone}>{config.label}</StatusPill>;
}

// =============================================================================
// Helpers
// =============================================================================
function isCustomLabel(label) {
    if (!label) return false;
    const trimmed = String(label).trim();
    if (!trimmed) return false;
    const defaults = new Set([
        'Booking pay',
        'Booking pay (hourly)',
        'Day shift',
        "Day's bookings",
        'Shift',
    ]);
    return !defaults.has(trimmed);
}

function buildItinerary(line) {
    const pickup = line.pickup_address || '';
    const dropoff = line.dropoff_address || '';
    const isHourly = line.booking_mode === 'hourly';
    if (isHourly) {
        const hrs = line.booking_hours != null ? Number(line.booking_hours) : null;
        const tag = hrs != null && hrs > 0 ? `${hrs}h` : 'hourly';
        return pickup ? `${shortenAddress(pickup)} · ${tag}` : tag;
    }
    if (!pickup && !dropoff) return '—';
    if (pickup && dropoff) return `${shortenAddress(pickup)} → ${shortenAddress(dropoff)}`;
    return shortenAddress(pickup || dropoff);
}

function shortenAddress(addr) {
    if (!addr) return '';
    const parts = String(addr).split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length <= 2) return parts.join(', ');
    return parts.slice(0, 2).join(', ');
}

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
}

function formatDateTime(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function formatPence(pence) {
    const n = Number(pence || 0);
    return `£${(n / 100).toFixed(2)}`;
}

function formatGBP(amount) {
    const n = Number(amount || 0);
    return `£${n.toFixed(2)}`;
}
